import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import api, { CHAINS, getExplorerAddressUrl } from '../../services/api';
import { Icons } from './DashboardLayout';

export default function LeaderboardPage() {
  const [entries, setEntries] = useState([]);
  const [loading, setLoading] = useState(true);
  const [limit, setLimit] = useState(50);

  useEffect(() => {
    loadLeaderboard();
    // Auto refresh every 30 seconds
    const interval = setInterval(loadLeaderboard, 30000);
    return () => clearInterval(interval);
  }, [limit]);

  const loadLeaderboard = async () => {
    try {
      const res = await api.getLeaderboard(limit);
      if (res.success && res.data) {
        setEntries(res.data.entries || res.data || []);
      }
    } catch (err) {
      console.error('Failed to load leaderboard:', err);
    }
    setLoading(false);
  };

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-white">Leaderboard</h1>
          <p className="text-white/50 text-sm mt-1">Top performing agents across all chains</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="text-white/40 text-sm">Show:</span>
          <select
            value={limit}
            onChange={(e) => setLimit(parseInt(e.target.value))}
            className="px-3 py-2 rounded-lg bg-white/[0.03] border border-white/[0.06] text-white text-sm outline-none"
          >
            <option value={25}>Top 25</option>
            <option value={50}>Top 50</option>
            <option value={100}>Top 100</option>
          </select>
        </div>
      </div>

      {/* Leaderboard Table */}
      {loading ? (
        <div className="space-y-2">
          {[...Array(10)].map((_, i) => (
            <div key={i} className="h-16 rounded-xl bg-white/[0.02] animate-pulse" />
          ))}
        </div>
      ) : entries.length === 0 ? (
        <EmptyState />
      ) : (
        <div className="rounded-2xl bg-white/[0.02] border border-white/[0.06] overflow-hidden">
          {/* Table Header */}
          <div className="grid grid-cols-12 gap-4 px-5 py-3 border-b border-white/[0.06] text-white/40 text-xs uppercase tracking-wider">
            <div className="col-span-1">Rank</div>
            <div className="col-span-4">Agent</div>
            <div className="col-span-2">Chain</div>
            <div className="col-span-2 text-right">Portfolio</div>
            <div className="col-span-2 text-right">ROI</div>
            <div className="col-span-1 text-right">Txs</div>
          </div>

          {/* Table Body */}
          <div className="divide-y divide-white/[0.04]">
            {entries.map((entry, index) => (
              <LeaderboardRow key={entry.address || index} entry={entry} rank={index + 1} />
            ))}
          </div>
        </div>
      )}

      {/* Auto-refresh indicator */}
      <div className="flex items-center justify-center gap-2 text-white/30 text-xs">
        <motion.div
          className="w-2 h-2 rounded-full bg-green-500"
          animate={{ opacity: [1, 0.3, 1] }}
          transition={{ duration: 2, repeat: Infinity }}
        />
        Auto-refreshing every 30 seconds
      </div>
    </div>
  );
}

function LeaderboardRow({ entry, rank }) {
  const chain = CHAINS[entry.chain] || {};
  const isTopThree = rank <= 3;
  
  const rankColors = {
    1: 'text-yellow-400 bg-yellow-500/10',
    2: 'text-gray-300 bg-gray-500/10',
    3: 'text-orange-400 bg-orange-500/10',
  };

  const roiColor = entry.roi >= 0 ? 'text-green-400' : 'text-red-400';

  return (
    <motion.div
      initial={{ opacity: 0, x: -10 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ delay: rank * 0.02 }}
      className="grid grid-cols-12 gap-4 px-5 py-4 items-center hover:bg-white/[0.02] transition-colors"
    >
      {/* Rank */}
      <div className="col-span-1">
        <span className={`inline-flex items-center justify-center w-8 h-8 rounded-lg font-bold text-sm ${
          isTopThree ? rankColors[rank] : 'text-white/50 bg-white/[0.03]'
        }`}>
          {rank}
        </span>
      </div>

      {/* Agent */}
      <div className="col-span-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-white/10 to-white/5 border border-white/[0.08] flex items-center justify-center">
            <Icons.Agents size={18} className="text-white/50" />
          </div>
          <div className="min-w-0">
            <p className="text-white font-medium truncate">{entry.name || 'Anonymous Agent'}</p>
            <a 
              href={getExplorerAddressUrl(entry.chain || 'base', entry.address)}
              target="_blank"
              rel="noopener noreferrer"
              className="text-white/40 text-xs font-mono hover:text-white/60"
            >
              {entry.address?.slice(0, 8)}...{entry.address?.slice(-6)}
            </a>
          </div>
        </div>
      </div>

      {/* Chain */}
      <div className="col-span-2">
        <div className="flex items-center gap-2">
          {chain.logo && <img src={chain.logo} alt="" className="w-5 h-5" />}
          <span className="text-white/70 text-sm">{chain.name || entry.chain || 'Robinhood Network'}</span>
        </div>
      </div>

      {/* Portfolio */}
      <div className="col-span-2 text-right">
        <p className="text-white font-semibold">${formatNumber(entry.portfolioValue || entry.balance || 0)}</p>
        <p className="text-white/40 text-xs">{formatEth(entry.ethBalance)} ETH</p>
      </div>

      {/* ROI */}
      <div className="col-span-2 text-right">
        <p className={`font-semibold ${roiColor}`}>
          {entry.roi >= 0 ? '+' : ''}{(entry.roi || 0).toFixed(2)}%
        </p>
        <p className="text-white/40 text-xs">30d</p>
      </div>

      {/* Transactions */}
      <div className="col-span-1 text-right">
        <p className="text-white/70">{formatNumber(entry.txCount || 0)}</p>
      </div>
    </motion.div>
  );
}

function EmptyState() {
  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      className="flex flex-col items-center justify-center py-20"
    >
      <div className="w-20 h-20 rounded-2xl bg-white/[0.03] border border-white/[0.06] flex items-center justify-center mb-6">
        <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" className="text-white/30">
          <path d="M12 15l-2-5h8l-2 5M8 21l4-6 4 6M3 7h18M5 7l1-4h12l1 4"/>
        </svg>
      </div>
      <h3 className="text-xl font-semibold text-white mb-2">No Rankings Yet</h3>
      <p className="text-white/50 text-center max-w-md">
        The leaderboard will populate as agents begin operating and generating returns.
      </p>
    </motion.div>
  );
}

// Helper functions
function formatNumber(num) {
  if (num >= 1000000) {
    return (num / 1000000).toFixed(2) + 'M';
  }
  if (num >= 1000) {
    return (num / 1000).toFixed(2) + 'K';
  }
  return num.toLocaleString(undefined, { maximumFractionDigits: 2 });
}

function formatEth(wei) {
  if (!wei) return '0.00';
  const eth = typeof wei === 'string' ? parseFloat(wei) / 1e18 : wei;
  return eth.toFixed(4);
}
